import clsx from "clsx";
import { FC } from "react";
import { useFragment } from "react-relay";
import { graphql } from "relay-runtime";
import {
  /* Decimal, */
  i18n,
  /* Select, */ useQueryParam,
} from "rujira.ui";
import { AngleDown } from "rujira.ui/src/components/icons/Icons";
import { Drawer } from "vaul";
import {} from "../../portfolio/utils";
import { OrderType, Side } from "../types";
import { SubmitClassicFragment$key } from "./__generated__/SubmitClassicFragment.graphql";
import { useCalcMetadata } from "./Calc";
import { SubmitLimit } from "./SubmitLimit";
import { SubmitMarket } from "./SubmitMarket";
import { SubmitOracle } from "./SubmitOracle";
import { SubmitRecurring } from "./SubmitRecurring";

const fragment = graphql`
  fragment SubmitClassicFragment on FinPair {
    ...SubmitMarketFragment
    ...SubmitOracleFragment
    ...SubmitLimitFragment
    ...SubmitRecurringFragment

    address
    tick
    assetBase {
      metadata {
        symbol
      }
    }
    assetQuote {
      metadata {
        symbol
      }
    }
    oracleBase {
      id
      price
    }
    oracleQuote {
      id
      price
    }
    book {
      bids {
        price
      }
      center
      asks {
        price
      }
    }
    feeMaker
    feeTaker
  }
`;

export const LaunchModal: FC<{
  k: SubmitClassicFragment$key;
  onClick: (s: Side) => void;
}> = ({ k, onClick }) => {
  const d = useFragment(fragment, k);

  return (
    <div className="trade__launch">
      <Drawer.Trigger
        className="button button--small button--blue w-full"
        onClick={() => onClick(Side.Quote)}>
        Buy {d.assetBase.metadata.symbol}
      </Drawer.Trigger>
      <Drawer.Trigger
        className="button button--small button--red w-full"
        onClick={() => onClick(Side.Base)}>
        Sell {d.assetBase.metadata.symbol}
      </Drawer.Trigger>
    </div>
  );
};

interface Props {
  k: SubmitClassicFragment$key;
  side: Side;
}

export const SubmitClassic: FC<Props> = (props) => {
  const [type, setType] = useQueryParam<OrderType>("order", OrderType.Market);
  const d = useFragment(fragment, props.k);

  const metadata = useCalcMetadata();

  const hasTrackingOrders = d.oracleBase && d.oracleQuote;
  const hasRecurringOrders = !!metadata;

  return (
    <div className="trade__submit-scroll">
      <nav className="tabs tabs--sm tabs--full row">
        <a
          className={clsx("col-4", {
            selected: type === OrderType.Market,
          })}
          onClick={() => {
            setType(OrderType.Market);
          }}>
          {i18n.t("Market")}
        </a>
        <a
          className={clsx("col-4", {
            selected: type === OrderType.Limit,
          })}
          onClick={() => {
            setType(OrderType.Limit);
          }}>
          {i18n.t("Limit")}
        </a>
        {!hasTrackingOrders && hasRecurringOrders && (
          <a
            className={clsx("flex ai-c", {
              selected: type === OrderType.Recurring,
            })}
            style={{
              fontSize: "0.75rem",
            }}
            onClick={() => {
              setType(OrderType.Recurring);
            }}>
            {i18n.t("Recurring")}
          </a>
        )}
        {hasTrackingOrders && !hasRecurringOrders && (
          <a
            className={clsx("flex ai-c", {
              selected: type === OrderType.Tracking,
            })}
            style={{
              fontSize: "0.75rem",
            }}
            onClick={() => {
              setType(OrderType.Tracking);
            }}>
            {i18n.t("Tracking")}
          </a>
        )}
        {hasTrackingOrders && hasRecurringOrders && (
          <a
            className={clsx("col-4", {
              selected:
                type === OrderType.Recurring || type === OrderType.Tracking,
            })}>
            {i18n.t(
              type === OrderType.Tracking
                ? "Tracking"
                : type === OrderType.Recurring
                  ? "Recurring"
                  : "Advanced"
            )}
            <AngleDown />
            <nav>
              {hasTrackingOrders && type !== OrderType.Tracking && (
                <a
                  className="flex ai-c"
                  style={{
                    fontSize: "0.75rem",
                  }}
                  onClick={() => {
                    setType(OrderType.Tracking);
                  }}>
                  {i18n.t("Tracking")}
                </a>
              )}
              {hasRecurringOrders && type !== OrderType.Recurring && (
                <a
                  className="flex ai-c"
                  style={{
                    fontSize: "0.75rem",
                  }}
                  onClick={() => {
                    setType(OrderType.Recurring);
                  }}>
                  {i18n.t("Recurring")}
                </a>
              )}
            </nav>
          </a>
        )}
      </nav>
      {type === OrderType.Limit && <SubmitLimit k={d} side={props.side} />}
      {type === OrderType.Tracking && d.oracleBase && d.oracleQuote && (
        <SubmitOracle
          k={d}
          side={props.side}
          oracleBase={BigInt(d.oracleBase.price)}
          oracleQuote={BigInt(d.oracleQuote.price)}
        />
      )}
      {type === OrderType.Market && <SubmitMarket k={d} side={props.side} />}
      {type === OrderType.Recurring && (
        <SubmitRecurring k={d} side={props.side} />
      )}
    </div>
  );
};

export const SubmitFallBack: FC = () => {
  return (
    <>
      <div className="trade__submit">
        <nav className="tabs tabs--sm tabs--full">
          <a className="skeleton" />
          <a className="skeleton" />
        </nav>

        <div className="mt-2">
          <div className="grow trade__submit-switch trade__submit-switch--quote">
            <button className="skeleton" />
            <button className="skeleton" />
          </div>
        </div>
        <div className="row wrap pad-mini mt-1.5">
          <div className="col">
            <div className="skeleton h-5 w-full br-1 block" />
          </div>

          <div className="flex ai-c px-0.5 mt-1 mt-xs-0 jc-lg-c mx-a">
            <div className="skeleton w-3.5 h-2.5 br-1" />
            <div className="skeleton w-3.5 h-2.5 br-1 ml-0.5" />
            <div className="skeleton w-3.5 h-2.5 br-1 ml-0.5" />
            <div className="skeleton w-3.5 h-2.5 br-1 ml-0.5" />
          </div>
        </div>

        <div className="skeleton h-5 w-full br-1 block mt-1" />
        <div className="skeleton h-5 w-full br-1 block mt-a" />
      </div>
    </>
  );
};
